import { useStore } from '../store/store';
import { useLocation } from 'react-router';
import { paths, routes } from '../utils/constants/routes';
import { ACCOUNT_VERIF_TYPES, ACCOUNT_TYPES } from '../utils/constants/auth';
import Reroute from '../components/navigation/Reroute';
import AppLoader from '../components/loaders/AppLoader';
import AppError from '../components/errors/AppError';
import { matchPath } from '../utils/utils';
import { HEADER_MESSAGE_TYPES } from '../utils/constants/header';
import { getMfaSetupRequired, isLinkedInIdentity } from '../api/cognito';

const staticPaths = [
  paths.TERMS_N_CONDITIONS,
  paths.COOKIE_POLICY,
  paths.PRIVACY_POLICY,
  paths.IMPRINT,
  paths.GDPR
];

const freeAuthPaths = [
  ...staticPaths,
  paths.HOME,
  paths.HOME_FULL,
  paths.FORGOTTEN_PASSWORD,
  paths.SIGNUP,
  paths.LOGIN,
  // TD-1976: the "Lost access to your authenticator?" recovery page is reached
  // from the login MFA challenge step, while the user is mid-authentication
  // (password verified, but Cognito has not issued a session yet). Treat it
  // like the other auth-free pages so the guard does not bounce them back to /.
  //
  // SECURITY: this route is reachable WITHOUT an authenticated session. The
  // page today is UI-only — it makes no network calls and submits no data.
  // If any future change wires a backend action here (e.g. "send me a
  // recovery email", "redeem a one-time recovery code"), the backend MUST
  // treat the caller as unauthenticated and apply:
  //   - server-side rate limiting per IP + per email, and
  //   - CAPTCHA on the request that triggers the action.
  // Otherwise this becomes a user-enumeration + brute-force entry point.
  paths.MFA_RECOVERY
];

const nonApprovalPaths = [...staticPaths, paths.USER_VERIF_PROGRESS, paths.MFA_SETUP];

const nonCompletedCompanyPaths = [
  ...staticPaths,
  paths.USER_VERIF_PROGRESS,
  paths.CREATE_COMPANY_PROFILE,
  paths.MFA_SETUP
];

const blockAuthPaths = [paths.FORGOTTEN_PASSWORD, paths.SIGNUP, paths.LOGIN];

const agentOnlyPaths = [paths.PROJECTS, paths.TEAM];

const investorOnlyPaths = [
  routes.FUNDS_PATH,
  routes.PORTFOLIO_COMPANIES_PATH,
  routes.INVEST_PROFILES_PATH,
  paths.ALL_INVESTMENTS
];

const masterOnlyPaths = [paths.ALL_INVESTMENTS];

const adminOnlyPaths = [paths.ADMIN_DASHBOARD];

const profilerOnlyPaths = [
  paths.PROFILER,
  paths.PROFILER_PROJECTS,
  paths.PROFILER_TEAM,
  paths.PROFILER_WATCH_LIST,
  routes.PROFILER_DETAILS_PATH,
  routes.INDIVIDUAL_DETAILS_PATH
];

const restoreAfterLoginPaths = [
  paths.OUTREACH,
  ...agentOnlyPaths,
  ...investorOnlyPaths,
  ...masterOnlyPaths,
  ...adminOnlyPaths,
  ...profilerOnlyPaths
];

const useAppRouteGuard = () => {
  const { authStore, utilsStore, profilerAccessStore } = useStore();
  const location = useLocation();
  const { isLoadingAccountDetails, accountInfoError, userVerificationStatus } = authStore;
  const { user, userAccountType, hasUserProfileCompany } = authStore;
  const { isMasterUser, isCognitoAdmin } = authStore.userInfo || {};

  if (isLoadingAccountDetails) {
    return <AppLoader />;
  }

  if (accountInfoError) {
    return <AppError />;
  }

  // TD-1976 — post-login MFA guard.
  // Lock authenticated users with `custom:mfa_setup_required="true"` to /mfa-setup.
  // LinkedIn-federated users bypass MFA at the Cognito layer by design; the LinkedIn
  // check is evaluated FIRST as a defensive short-circuit in case the claim is ever
  // set on a LinkedIn user.
  if (
    user &&
    !isLinkedInIdentity(user) &&
    getMfaSetupRequired(user) &&
    !matchPath([paths.MFA_SETUP], location)
  ) {
    return <Reroute path={paths.MFA_SETUP} />;
  }

  if (
    user &&
    userVerificationStatus !== ACCOUNT_VERIF_TYPES.APPROVED &&
    !matchPath(nonApprovalPaths, location)
  ) {
    return <Reroute path={paths.USER_VERIF_PROGRESS} />;
  }

  if (
    user &&
    isMasterUser &&
    !isLoadingAccountDetails &&
    !hasUserProfileCompany &&
    userVerificationStatus === ACCOUNT_VERIF_TYPES.APPROVED &&
    !matchPath(nonCompletedCompanyPaths, location)
  ) {
    return <Reroute path={paths.CREATE_COMPANY_PROFILE} />;
  }

  if (user && Object.keys(authStore.userInfo || {}).length) {
    if (window.initialPage) {
      if (
        window.initialPage.pathname === location.pathname &&
        window.initialPage.search === location.search
      ) {
        window.initialPage = null;
      } else {
        if (matchPath(restoreAfterLoginPaths, window.initialPage)) {
          let redirectPath = window.initialPage.pathname;
          if (window.initialPage.search) {
            redirectPath += window.initialPage.search;
          }
          window.initialPage = null;
          return <Reroute path={redirectPath} />;
        } else {
          window.initialPage = null;
        }
      }
    }
  }

  if (!user && !matchPath(freeAuthPaths, location)) {
    return <Reroute path={paths.HOME} />;
  }

  if (user && matchPath(blockAuthPaths, location)) {
    return <Reroute path={paths.HOME_FULL} />;
  }

  if (userAccountType !== ACCOUNT_TYPES.INVESTOR && matchPath(investorOnlyPaths, location)) {
    return <Reroute path={user ? paths.HOME_FULL : paths.HOME} />;
  }

  if (userAccountType !== ACCOUNT_TYPES.AGENT && matchPath(agentOnlyPaths, location)) {
    return <Reroute path={user ? paths.HOME_FULL : paths.HOME} />;
  }

  if (!isMasterUser && matchPath(masterOnlyPaths, location)) {
    return <Reroute path={user ? paths.HOME_FULL : paths.HOME} />;
  }

  if (!isCognitoAdmin && matchPath(adminOnlyPaths, location)) {
    return <Reroute path={user ? paths.HOME_FULL : paths.HOME} />;
  }

  if (!profilerAccessStore.hasAnyProfilerAccess && matchPath(profilerOnlyPaths, location)) {
    utilsStore.setHeaderMessage(
      `Page ${location.pathname} requires Profiler access.`,
      HEADER_MESSAGE_TYPES.WARNING
    );
    return <Reroute path={user ? paths.HOME_FULL : paths.HOME} />;
  }

  if (
    !profilerAccessStore.canViewProfilerWatchlist &&
    matchPath([paths.PROFILER_WATCH_LIST], location)
  ) {
    utilsStore.setHeaderMessage(
      `Page ${location.pathname} requires Profiler Lite or Profiler Full access.`,
      HEADER_MESSAGE_TYPES.WARNING
    );
    return <Reroute path={user ? paths.HOME_FULL : paths.HOME} />;
  }

  if (
    !profilerAccessStore.canUseProfilerSearch &&
    matchPath([paths.PROFILER_PROJECTS, paths.PROFILER], location)
  ) {
    utilsStore.setHeaderMessage(
      `Page ${location.pathname} requires Profiler Lite or Profiler Full access.`,
      HEADER_MESSAGE_TYPES.WARNING
    );
    return <Reroute path={user ? paths.HOME_FULL : paths.HOME} />;
  }

  // This will guard the search results page from INVESTOR users without profiler access
  if (
    userAccountType === ACCOUNT_TYPES.INVESTOR &&
    !profilerAccessStore.canUseProfilerSearch &&
    matchPath([paths.SEARCH_RESULTS], location)
  ) {
    utilsStore.setHeaderMessage(
      `Page ${location.pathname} requires Profiler Lite or Profiler Full access.`,
      HEADER_MESSAGE_TYPES.WARNING
    );
    return <Reroute path={user ? paths.HOME_FULL : paths.HOME} />;
  }

  return null;
};

export default useAppRouteGuard;
